import React, { useState, useEffect } from 'react';
import { Edit } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { convertToAbsoluteGrade, convertToRelativeGrade, getGradeOptions, getSchoolTypeFromName } from '../../utils/gradeUtils';
import { Modal, Button } from '../common';
import SchoolSearchInput from './SchoolSearchInput';
import SchoolNameUtils from '../../utils/schoolNameUtils';
import schoolList from '../../data/school_list.json';

function EditObjectModal({ isOpen, onClose, exam, onSuccess }) {
  const [formData, setFormData] = useState({
    school_name: '',
    att_year: '',
    school_grade: '',
    phase: '',
    subject: '',
    edu_version: '',
    school_type: ''
  });
  const [originalData, setOriginalData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();

  const [examType, setExamType] = useState('school'); // 'school' or 'workbook'
  const [selectedSchool, setSelectedSchool] = useState(null);

  const phases = [
    { value: '11', label: '1학기 중간' },
    { value: '12', label: '1학기 기말' },
    { value: '13', label: '1학기 기타' },
    { value: '21', label: '2학기 중간' },
    { value: '22', label: '2학기 기말' },
    { value: '23', label: '2학기 기타' }
  ];

  const schoolTypes = [
    { value: '1', label: '초등학교' },
    { value: '2', label: '중학교' },
    { value: '3', label: '고등학교' }
  ];

  // 시험 데이터로 폼 초기화
  useEffect(() => {
    if (exam) {
      // 백엔드 절대 학년을 UI 상대 학년으로 변환
      const gradeInfo = convertToRelativeGrade(exam.school_grade);
      const schoolType = gradeInfo.schoolType || getSchoolTypeFromName(exam.school_name);
      
      const data = {
        school_name: exam.school_name || '',
        att_year: exam.att_year?.toString() || '',
        school_grade: gradeInfo.relativeGrade?.toString() || '',
        phase: exam.phase?.toString() || '',
        subject: exam.subject || '',
        edu_version: exam.edu_version?.toString() || '',
        school_type: schoolType?.toString() || ''
      };
      setFormData(data);
      setOriginalData(data);
    }
  }, [exam]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // 학교 타입이 변경되면 학년 선택을 초기화
    if (name === 'school_type') {
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        school_grade: '' // 학년 초기화
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // 변경된 필드들 찾기 (절대 학년으로 변환하여 비교)
  const getChangedFields = () => {
    const changes = [];
    
    // 현재 school_type과 원래 school_type
    const currentSchoolType = formData.school_type || getSchoolTypeFromName(formData.school_name);
    const originalSchoolType = originalData.school_type || getSchoolTypeFromName(originalData.school_name);
    
    Object.keys(formData).forEach(key => {
      // school_type은 수정 불가능한 필드이므로 제외
      if (key === 'school_type') return;
      
      let newValue = formData[key];
      let oldValue = originalData[key];
      
      // 학년 필드의 경우 절대 학년으로 변환하여 비교
      if (key === 'school_grade') {
        if (newValue && oldValue) {
          // 새 값: 현재 선택된 school_type으로 절대 학년 계산
          newValue = convertToAbsoluteGrade(currentSchoolType, parseInt(newValue)).toString();
          // 기존 값: 원래 school_type으로 절대 학년 계산
          oldValue = convertToAbsoluteGrade(originalSchoolType, parseInt(oldValue)).toString();
        }
      }
      
      if (newValue !== oldValue) {
        let apiValue = formData[key];
        
        // API 호출 시 학년은 절대 학년으로 변환
        if (key === 'school_grade') {
          apiValue = convertToAbsoluteGrade(currentSchoolType, parseInt(formData[key])).toString();
        }
        
        changes.push({
          update_key: key,
          update_value: apiValue
        });
      }
    });
    
    return changes;
  };

  const handleSubmit = async () => {
    const changes = getChangedFields();
    
    if (changes.length === 0) {
      alert('변경된 내용이 없습니다.');
      return;
    }

    try {
      setIsLoading(true);
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      // 각 변경사항에 대해 순차적으로 API 호출
      for (const change of changes) {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/schoolexams/${exam.sxm_id}`,
          {
            method: 'PUT',
            headers: {
              'Authorization': token,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(change)
          }
        );

        if (response.status === 401) {
          navigate('/signin');
          return;
        }

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`${change.update_key} 수정 실패: ${errorData.message}`);
        }
      }

      alert(`${changes.length}개 필드가 성공적으로 수정되었습니다.`);
      handleClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error updating exam object:', error);
      alert(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      school_name: '',
      att_year: '',
      school_grade: '',
      phase: '',
      subject: '',
      edu_version: '',
      school_type: ''
    });
    setOriginalData({});
    onClose();
  };

  if (!isOpen || !exam) return null;

  const changedFields = getChangedFields();

  // 현재 선택된 학교 타입에 따른 학년 옵션
  const currentSchoolType = formData.school_type || getSchoolTypeFromName(formData.school_name);
  const gradeOptions = getGradeOptions(currentSchoolType);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="시험 객체 수정"
      size="lg"
      preventScroll={true}
      zIndex={9999940}
      isMobileView={window.innerWidth < 768}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            취소
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={changedFields.length === 0 || isLoading}
            loading={isLoading}
            className="bg-primary hover:bg-[#3da575]"
          >
            {changedFields.length > 0 ? `${changedFields.length}개 필드 수정` : '수정하기'}
          </Button>
        </>
      }
    >
      <div className="mb-6 rounded border border-gray-200 bg-gray-50 p-4">
        <h3 className="mb-2 font-semibold text-gray-800">현재 정보</h3>
        <div className="space-y-1 text-sm text-gray-700">
          <p><strong>ID:</strong> {exam.sxm_id}</p>
          <p><strong>생성일:</strong> {new Date(exam.created_at).toLocaleDateString()}</p>
          <p><strong>파일 상태:</strong> {exam.file_exists ? '있음' : '없음'}</p>
          {changedFields.length > 0 && (
            <p className="font-medium text-orange-600">
              {changedFields.length}개 필드 수정 예정
            </p>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            학교명/문제집명 <span className="text-red-500">*</span>
          </label>
          <SchoolSearchInput
            value={formData.school_name}
            onChange={(value) => {
              setFormData(prev => ({ ...prev, school_name: value }));
            }}
            onSchoolSelect={(school, abbreviation) => {
              setSelectedSchool(school);
              setFormData(prev => ({ 
                ...prev, 
                school_name: school.school_name,  // 전체 학교명 저장
                school_type: school.school_level === '초등학교' ? '1' :
                            school.school_level === '중학교' ? '2' : '3'
              }));
            }}
            onWorkbookChange={(name) => {
              setFormData(prev => ({ ...prev, school_name: name, school_type: '' }));
            }}
            placeholder="학교명 검색"
            required
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            학교 타입 <span className="text-red-500">*</span>
          </label>
          <select
            name="school_type"
            value={formData.school_type}
            onChange={handleInputChange}
            className="w-full rounded border p-2"
            required
          >
            <option value="">학교 타입 선택</option>
            {schoolTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            학교 타입 변경 시 학년이 초기화됩니다.
          </p>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            출제년도 <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            name="att_year"
            value={formData.att_year}
            onChange={handleInputChange}
            placeholder="예: 2025"
            className="w-full rounded border p-2"
            min="2016"
            max="2030"
            required
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            학년 <span className="text-red-500">*</span>
            {!formData.school_type && (
              <span className="ml-2 text-xs text-orange-500">(학교 타입을 먼저 선택하세요)</span>
            )}
          </label>
          <select
            name="school_grade"
            value={formData.school_grade}
            onChange={handleInputChange}
            className="w-full rounded border p-2"
            required
            disabled={!formData.school_type}
          >
            <option value="">학년 선택</option>
            {gradeOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          {!formData.school_type ? (
            <p className="mt-1 text-xs text-orange-500">
              학교 타입을 선택해야 학년을 선택할 수 있습니다.
            </p>
          ) : (
            <p className="mt-1 text-xs text-gray-500">
              절대 학년으로 변환: {formData.school_grade ? convertToAbsoluteGrade(formData.school_type, parseInt(formData.school_grade)) : '-'}학년
            </p>
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            시험구분 <span className="text-red-500">*</span>
          </label>
          <select
            name="phase"
            value={formData.phase}
            onChange={handleInputChange}
            className="w-full rounded border p-2"
            required
          >
            <option value="">시험구분 선택</option>
            {phases.map(phase => (
              <option key={phase.value} value={phase.value}>{phase.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            과목 (선택)
          </label>
          <input
            type="text"
            name="subject"
            value={formData.subject}
            onChange={handleInputChange}
            placeholder="예: 수학, 영어, 국어..."
            className="w-full rounded border p-2"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            교육과정 (선택)
          </label>
          <input
            type="number"
            name="edu_version"
            value={formData.edu_version}
            onChange={handleInputChange}
            placeholder="예: 2022 (2022 개정 교육과정)"
            className="w-full rounded border p-2"
          />
          <p className="mt-1 text-xs text-gray-500">
            미입력 시 출제년도에 따라 자동 계산됩니다.
          </p>
        </div>
      </div>

      {changedFields.length > 0 && (
        <div className="mt-4 rounded border border-orange-200 bg-orange-50 p-3">
          <h4 className="mb-2 font-semibold text-orange-800">변경 예정 항목</h4>
          <div className="space-y-1 text-sm">
            {changedFields.map((change, index) => {
              const currentSchoolType = formData.school_type || getSchoolTypeFromName(formData.school_name);
              const originalSchoolType = originalData.school_type || getSchoolTypeFromName(originalData.school_name);
              
              let displayOldValue = originalData[change.update_key] || '(없음)';
              let displayNewValue = change.update_value || '(없음)';
              let fieldName = change.update_key;
              
              // 학년 필드는 특별히 처리
              if (change.update_key === 'school_grade') {
                fieldName = 'school_grade (절대학년)';
                if (originalData[change.update_key]) {
                  const originalAbsolute = convertToAbsoluteGrade(originalSchoolType, parseInt(originalData[change.update_key]));
                  displayOldValue = `${originalAbsolute}학년 (${schoolTypes.find(t => t.value === originalSchoolType)?.label} ${originalData[change.update_key]}학년)`;
                }
                displayNewValue = `${change.update_value}학년 (${schoolTypes.find(t => t.value === currentSchoolType)?.label} ${formData[change.update_key]}학년)`;
              }
              
              return (
                <p key={index} className="text-orange-700">
                  <strong>{fieldName}:</strong> {displayOldValue} → {displayNewValue}
                </p>
              );
            })}
          </div>
        </div>
      )}
    </Modal>
  );
}

export default EditObjectModal; 